import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AdminService } from '../admin/admin.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { SnackbarService } from 'src/app/core/snackbar.service';

import { COVARIATE_ROLE_ENUM, IExtendedUserList } from '@models/general/user';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthErrors_ENUM } from '@models/mood/helpers/errors';

interface UserConfigForm {
  login: FormControl<string | null>;
  email: FormControl<string | null>;
  firstname?: FormControl<string | null>;
  surname?: FormControl<string | null>;
  isadmin: FormControl<boolean | null>;
  disabled: FormControl<boolean | null>;
  password?: FormControl<string | null>;
  password_confirm?: FormControl<string | null>;
  covariate_role?: FormControl<string | null>;
}

@Component({
  selector: 'mood-user-form-admin',
  templateUrl: './user-form.component.html',
  styleUrls: ['user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserFormAdminComponent implements OnInit {
  @Output() userSavedEmitter: EventEmitter<any> = new EventEmitter();

  private _userId!: string;

  @Input() set userId(value: string) {
    this._userId = value;

    this._loadAndPatchExistingData();
  };

  public get userId() {
    return this._userId;
  }

  currentUser?: IExtendedUserList;

  userForm: FormGroup;
  public covariateRoles = Object.values(COVARIATE_ROLE_ENUM);

  constructor(
    private _snackbarService: SnackbarService,
    private _adminService: AdminService,
    private _changeDetector: ChangeDetectorRef,
    private _authService: AuthService)
    {
      this.userForm = new FormGroup<UserConfigForm>({
        login: new FormControl<string | null>(null, [Validators.required]),
        email: new FormControl<string | null>(null, [Validators.required]),
        firstname: new FormControl<string | null>(null),
        surname: new FormControl<string | null>(null),
        isadmin: new FormControl<boolean | null>(false, [Validators.required]),
        disabled: new FormControl<boolean | null>(true, [Validators.required]),
        password: new FormControl<string | null>(null),
        password_confirm: new FormControl<string | null>(null),
        covariate_role: new FormControl<string | null>(null),
      });
  }

  get saveButtonDisabled() {
    return this.userForm.invalid || this.userForm.pristine;
  }

  async ngOnInit() {
    if(this.userId === 'new') {
      this.userForm.controls['password']!.setValidators([Validators.required]);
      this.userForm.controls['password_confirm']!.setValidators([Validators.required]);

      this.userForm.addValidators(this.ConfirmedValidator());
    }
    else{
      this.userForm.controls['password']!.clearValidators();
      this.userForm.controls['password_confirm']!.clearValidators();
    }
  }

  private _clearExistingForm() {
    this.userForm.reset();
  }

  private async _loadAndPatchExistingData() {
    if(this.userId !== 'new') {
      this._clearExistingForm();
      this.currentUser = await this._adminService.findUserById(this.userId)! as IExtendedUserList

      if (!this.currentUser) {
        this._snackbarService.openErrorMessage("User not found");
      }

      this.currentUser.disabled = !this.currentUser.disabled;
      this.userForm.patchValue(this.currentUser!);
      this.userForm.markAsPristine();
    }
  }

  async saveForm() {
    let result: IExtendedUserList | undefined;
    const rawValues = this.userForm.getRawValue();
    rawValues.disabled = !rawValues.disabled;

    const isNewUser = this.userId === 'new';
    const action = isNewUser
      ? () => this._authService.registerUser(rawValues)
      : () => this._adminService.updateUser(this.userId, rawValues);
    const successMessage = isNewUser ? "User created" : "User updated";

    try {
      result = await action();
      this._snackbarService.openSuccessMessage(successMessage);

      if (result) {
        await this._adminService.updateUserById(this.userId, result);
        this.userForm.markAsPristine();
        this.currentUser = result;
        this._changeDetector.markForCheck();
      }
      this.userSavedEmitter.emit();
    }
    catch (error) {
      if (error instanceof HttpErrorResponse && error.error) {
        this.setErrorMessage(error);
      }
    }


  }


  ConfirmedValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const password = formGroup.get('password') as FormControl;
      const confirmPassword = formGroup.get('password_confirm') as FormControl;

      let passwordsMatching = password.value === confirmPassword.value;

      if (passwordsMatching) {
        password.setErrors(null);
        confirmPassword.setErrors(null);
      } else {
        password.setErrors({ notMatching: true });
        confirmPassword.setErrors({ notMatching: true });
      }
      return null;
    }
  }

  public getErrorMessage(control: AbstractControl | null) {
    if (control!.getError("error")) {
      return control!.getError("error");
    }
    else if (control!.hasError("login")) {
      return "Please provide a valid login";
    }
    else if (control!.hasError("email")) {
      return "Please provide a valid email address";
    }
    else if (control!.hasError("notMatching")) {
      return "The passwords do not match";
    }
    else {
      return "This field is required";
    }
  }

  public setErrorMessage(error: HttpErrorResponse){
    let errorObject = error.error as { message: string, code: AuthErrors_ENUM };
    switch (errorObject.code) {
      case AuthErrors_ENUM.LoginAlreadyExists:
        this.loginControl?.setErrors({ error: errorObject.message });
        break;

      case AuthErrors_ENUM.EmailAlreadyExists:
        this.emailControl?.setErrors({ error: errorObject.message });
        break;

      case AuthErrors_ENUM.BothAlreadyExists:
        this.loginControl?.setErrors({ error: errorObject.message });
        this.emailControl?.setErrors({ error: errorObject.message });
        break;
    }

    this.userForm.markAllAsTouched();
  }

  public get emailControl() {
    return this.userForm.get('email');
  }

  public get loginControl() {
    return this.userForm.get('login');
  }

  public get passwordControl() {
    return this.userForm.get('password');
  }

  public get confirmPasswordControl() {
    return this.userForm.get('password_confirm');
  }

  public get isAdmin() {
    return this._authService.userHasAdminRole;
  }
}
